/**
 * The ICE servers a peer connection is built with.
 *
 * relay-server's `GET /api/relay/calls/ice` answers STUN, and TURN when the
 * deployment has a key, with a `ttl` in seconds (the contract is
 * `relay-server/docs/calls-and-meetings.md` § ICE servers). The answer is held
 * for half its `ttl`, so a TURN credential never expires in the middle of a
 * call, and is held per credential: a guest's answer is not an account's.
 *
 * Any failure gives STUN alone, which is what every call used before TURN.
 * A call that connects on most networks beats one that cannot start.
 */

import { api, currentMeetingToken } from '../api';
import type { Request } from './sfu';

export const STUN_FALLBACK: RTCIceServer[] = [{ urls: 'stun:stun.cloudflare.com:3478' }];

interface IceAnswer {
  iceServers: RTCIceServer[];
  ttl: number;
}

const usable = (a: unknown): a is IceAnswer => {
  const x = a as Partial<IceAnswer> | null;
  return (
    !!x &&
    Array.isArray(x.iceServers) &&
    x.iceServers.length > 0 &&
    typeof x.ttl === 'number' &&
    x.ttl > 0
  );
};

export function iceSource(
  request: Request,
  now: () => number = () => Date.now(),
  credential: () => string | null = currentMeetingToken
): () => Promise<RTCIceServer[]> {
  let cached: { key: string | null; servers: RTCIceServer[]; until: number } | null = null;

  return async () => {
    const key = credential();
    if (cached && cached.key === key && now() < cached.until) return cached.servers;
    cached = null;
    try {
      const answer = await request<unknown>('/api/relay/calls/ice');
      if (!usable(answer)) return STUN_FALLBACK;
      cached = { key, servers: answer.iceServers, until: now() + answer.ttl * 500 };
      return answer.iceServers;
    } catch {
      return STUN_FALLBACK;
    }
  };
}

/** The page's one source, shared by every call and meeting it opens. */
export const iceServers = iceSource(api);
